import React from 'react';
import { Box, Paper, Typography, Button, Alert, Collapse } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Home as HomeIcon,
  BugReport as BugReportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

/**
 * ErrorBoundary
 *
 * Stops a single component crash from white-screening the whole app. When a
 * descendant throws during render, lifecycle, or constructor, this catches
 * it, posts a report to /api/client-errors, and shows a recovery UI with
 * "Go to Dashboard" and "Try again" buttons. The boundary resets its state
 * automatically when `resetKey` changes (we wire it to the route pathname),
 * so navigating to a different page recovers without a hard refresh.
 *
 * Tom Clancy 2026-04-30: PMs were reporting that some pages crash and the
 * site goes dead. Two of the underlying bugs are already fixed (MUI v9
 * Autocomplete params.InputProps, redaction middleware leaking {} for Date
 * columns). This boundary contains the blast radius for anything we miss.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      detailsOpen: false,
      reportSent: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    this.reportToServer(error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Reset when the route changes so navigating away clears the error.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        detailsOpen: false,
        reportSent: false,
      });
    }
  }

  async reportToServer(error, errorInfo) {
    try {
      const token = localStorage.getItem('token');
      const body = {
        message: String(error?.message || error || 'Unknown error'),
        stack: String(error?.stack || ''),
        component_stack: String(errorInfo?.componentStack || ''),
        url: typeof window !== 'undefined' ? window.location.href : '',
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        timestamp: new Date().toISOString(),
        boundary: 'react',
      };
      await fetch('/api/client-errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        // Best-effort; never block the recovery UI on this.
        keepalive: true,
      });
      this.setState({ reportSent: true });
    } catch (_) {
      /* swallow — the error UI is what the user sees, not this */
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, errorInfo, detailsOpen, reportSent } = this.state;

    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
        <Paper elevation={2} sx={{ p: 3, borderTop: '4px solid', borderColor: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <BugReportIcon color="error" sx={{ fontSize: 32 }} />
            <Typography variant="h5" fontWeight="bold">Something went wrong on this page.</Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            The page hit an unexpected error and stopped rendering. Other parts of the app
            should still work — you can navigate away or try reloading this page.
          </Typography>

          {reportSent && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              The error has been reported automatically. No further action needed.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => { window.location.href = '/'; }}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              Reload this page
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => window.history.back()}
            >
              Go back
            </Button>
          </Box>

          <Box>
            <Button
              size="small"
              onClick={() => this.setState({ detailsOpen: !detailsOpen })}
              endIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {detailsOpen ? 'Hide technical details' : 'Show technical details'}
            </Button>
            <Collapse in={detailsOpen}>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                <strong>{String(error?.message || error)}</strong>
                {error?.stack && (
                  <>
                    {'\n\n'}
                    {String(error.stack)}
                  </>
                )}
                {errorInfo?.componentStack && (
                  <>
                    {'\n\n--- React component stack ---'}
                    {String(errorInfo.componentStack)}
                  </>
                )}
              </Box>
            </Collapse>
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
